import React, { useState, useEffect } from 'react'
import {
  Box,
  Typography,
  Paper,
  Container,
  Button,
  Fade,
  Grow,
  useTheme,
  alpha,
  Dialog,
  DialogContent,
} from '@mui/material'
import {
  CardMembership,
  Payment,
  WarningAmber,
} from '@mui/icons-material'
import { useNavigate } from 'react-router-dom'
import { membershipApi } from '../../services/api'
import SubscriptionPlans from '../SubscriptionPlans'
import PaymentModal from '../contracts/PaymentModal'
import { MembershipPlan } from '../../types/subscription'
import api from '../../services/api'
import { useAuth } from '@/hooks/useAuth'
import { OnboardingStepId } from '../../types/onboarding'
import { useOnboarding } from '../../hooks/useOnboarding'
import { Alert } from '@mui/material'

interface SubscriptionRequiredPopupProps {
  providerName?: string
}

/**
 * Beautiful yellow popup component that displays when a provider
 * has not taken a subscription.
 */
const SubscriptionRequiredPopup: React.FC<SubscriptionRequiredPopupProps> = ({
  providerName,
}) => {
  const theme = useTheme()
  const navigate = useNavigate()
  const { user } = useAuth()
  const { markStepComplete } = useOnboarding()

  // State for subscription modal
  const [showSubscriptionDialog, setShowSubscriptionDialog] = useState(false)
  const [selectedPlan, setSelectedPlan] = useState<MembershipPlan | null>(null)
  const [billingCycle, setBillingCycle] = useState<'monthly' | 'yearly'>('monthly')
  const [isProcessingPayment, setIsProcessingPayment] = useState(false)
  const [paymentError, setPaymentError] = useState<string | null>(null)
  const [showPaymentModal, setShowPaymentModal] = useState(false)
  const [subscriptionDetails, setSubscriptionDetails] = useState<{
    orderId: string
    client_secret: string
    customerId: string
    ephemeralKey?: string
    discountedPrice?: string
  } | null>(null)
  const [therapistProfile, setTherapistProfile] = useState<any>(null)

  // Load therapist profile for billing address
  useEffect(() => {
    const loadProfile = async () => {
      if (!user?.id) return
      try {
        const res = await api.get('/therapist/profile')
        const data = res.data?.data || res.data
        setTherapistProfile(data)
      } catch (e) {
        console.error('Failed to load provider profile:', e)
      }
    }
    loadProfile()
  }, [user?.id])

  // Handle Choose Subscription
  const handleChooseSubscription = () => {
    setShowSubscriptionDialog(true)
  }

  // Handle plan selection from subscription dialog
  const handlePlanSelect = async (
    plan: MembershipPlan,
    cycle: 'monthly' | 'yearly',
    amountPaid: string,
    totalPrice: string,
    couponApplied: string
  ) => {
    if (!user?.id) {
      setPaymentError('User not found. Please log in again.')
      return
    }

    try {
      setIsProcessingPayment(true)
      setPaymentError(null)
      setBillingCycle(cycle)
      setSelectedPlan(plan)
      // Close subscription dialog immediately
      setShowSubscriptionDialog(false)
      // Open payment modal immediately with loading state
      setShowPaymentModal(true)

      // Step 1: Fetch price IDs for the selected plan
      const priceResponse = await membershipApi.getMembershipSubscriptionPrice(
        plan._id
      )
      const prices = priceResponse.data

      // Find the appropriate price ID based on billing cycle
      const selectedPrice = prices.find(
        p => p.billing_frequency.toLowerCase() === cycle
      )

      if (!selectedPrice) {
        throw new Error(`No price found for ${cycle} billing`)
      }

      // Step 2: Prepare billing address from profile
      const billingAddress = {
        address1:
          therapistProfile?.billingAddressLine ||
          therapistProfile?.addressLine ||
          '',
        address2: '',
        city: therapistProfile?.billingCity || therapistProfile?.city || '',
        state: therapistProfile?.billingState || therapistProfile?.state || '',
        postal_code:
          therapistProfile?.billingZipCode || therapistProfile?.zipCode || '',
        country: 'US',
      }

      // Step 3: Create subscription
      const subscriptionResponse = await membershipApi.createSubscription({
        therapist_id: user.id,
        members_price: [{ price_id: selectedPrice.stripe_price_id }],
        total_price: parseFloat(totalPrice) ?? parseFloat(selectedPrice.price),
        amount_paid: parseFloat(amountPaid) ?? parseFloat(selectedPrice.price),
        billing_address: billingAddress,
        ...(couponApplied !== '' && {
          coupon: couponApplied,
        }),
      })

      if (!subscriptionResponse.client_secret) {
        throw new Error('Failed to create subscription payment intent')
      }

      // Step 4: Store subscription details and show payment modal
      setSubscriptionDetails({
        orderId: subscriptionResponse.orderId,
        client_secret: subscriptionResponse.client_secret,
        customerId: subscriptionResponse.customerId,
        ephemeralKey: subscriptionResponse.ephemeralKey,
      })
    } catch (error: any) {
      console.error('Error in plan selection flow:', error)
      const errorMessage =
        error instanceof Error ? error.message : 'An error occurred'
      setPaymentError(errorMessage)
      setShowPaymentModal(false)
    } finally {
      setIsProcessingPayment(false)
    }
  }

  // Handle payment success
  const handlePaymentSuccess = async (paymentIntentId: string) => {
    try {
      console.log('Payment successful! Intent ID:', paymentIntentId)

      // Mark subscription step as complete
      await markStepComplete(OnboardingStepId.SUBSCRIPTION_PLAN)

      // Close payment modal
      setShowPaymentModal(false)
      setSubscriptionDetails(null)
      setSelectedPlan(null)
      
      // Reload page to update verification status (since ProviderVerificationGuard will re-check)
      window.location.reload()
    } catch (error) {
      console.error('Error after payment:', error)
      setPaymentError('Error processing your payment. Please contact support.')
    }
  }

  // Custom yellow colors for a "premium yellow" look
  const yellowMain = '#FFC107' // Amber 500
  const yellowDark = '#FFA000' // Amber 700
  const yellowLight = '#FFECB3' // Amber 100

  return (
    <Dialog
      open={true}
      maxWidth="sm"
      fullWidth
      disableEscapeKeyDown
      PaperProps={{
        sx: {
          borderRadius: 6,
          overflow: 'hidden',
          border: `1px solid ${alpha(yellowMain, 0.4)}`,
          boxShadow: `0 24px 48px ${alpha(yellowMain, 0.25)}`,
          position: 'relative',
        }
      }}
    >
      {/* Decorative background elements */}
      <Box sx={{
        position: 'absolute',
        top: -100,
        left: -100,
        width: 300,
        height: 300,
        borderRadius: '50%',
        background: `radial-gradient(circle, ${alpha(yellowMain, 0.1)} 0%, transparent 70%)`,
        zIndex: 0,
      }} />

      <Box
        sx={{
          background: `linear-gradient(135deg, ${yellowMain} 0%, ${yellowDark} 100%)`,
          p: 3,
          textAlign: 'center',
          position: 'relative',
          overflow: 'hidden',
          zIndex: 1,
        }}
      >
        {/* Animated icon container */}
        <Grow in timeout={1000}>
          <Box
            sx={{
              display: 'inline-flex',
              p: 2,
              borderRadius: '40%',
              bgcolor: 'rgba(255, 255, 255, 0.95)',
              mb: 1.5,
              boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
              transform: 'rotate(-5deg)',
              transition: 'transform 0.3s ease',
              '&:hover': {
                transform: 'rotate(0deg) scale(1.05)',
              }
            }}
          >
            <CardMembership sx={{ fontSize: 48, color: yellowDark }} />
          </Box>
        </Grow>

        <Fade in timeout={1200}>
          <Typography
            variant="h4"
            sx={{
              color: '#000',
              fontWeight: 900,
              mb: 1.5,
              letterSpacing: '-0.02em',
              textShadow: '0 1px 2px rgba(255,255,255,0.3)'
            }}
          >
            Subscription Required
          </Typography>
        </Fade>

        <Fade in timeout={1400}>
          <Typography variant="subtitle1" sx={{ color: 'rgba(0,0,0,0.7)', fontWeight: 500, maxWidth: '80%', mx: 'auto' }}>
            Unlock your potential and start your professional journey with us today.
          </Typography>
        </Fade>
      </Box>

      <DialogContent sx={{ p: 3.5, zIndex: 1, bgcolor: '#fff' }}>
        {providerName && (
          <Typography variant="h5" sx={{ mb: 1, fontWeight: 700, color: '#1A1A1A' }}>
            Hello, {providerName}
          </Typography>
        )}
        <Typography variant="body1" sx={{ mb: 2.5, color: '#4A4A4A', lineHeight: 1.6, fontSize: '1rem' }}>
          To access the platform's exclusive features like job listings, client management, and automated scheduling, please activate your subscription.
        </Typography>

        <Paper
          elevation={0}
          sx={{
            p: 2,
            mb: 3.5,
            bgcolor: alpha(yellowMain, 0.08),
            border: `1px solid ${alpha(yellowMain, 0.3)}`,
            borderRadius: 3,
            display: 'flex',
            alignItems: 'center',
            gap: 2.5,
          }}
        >
          <Box sx={{
            bgcolor: yellowMain,
            borderRadius: '50%',
            p: 1,
            display: 'flex',
            boxShadow: '0 4px 10px rgba(251, 192, 45, 0.3)'
          }}>
            <WarningAmber sx={{ color: '#fff', fontSize: 24 }} />
          </Box>
          <Typography variant="body2" sx={{ color: '#5D4037', fontWeight: 600, lineHeight: 1.5 }}>
            Access to verified provider sections is currently restricted until a subscription plan is active.
          </Typography>
        </Paper>

        <Box sx={{ display: 'flex', gap: 1.5, flexDirection: 'column' }}>
          <Button
            fullWidth
            variant="contained"
            size="large"
            onClick={handleChooseSubscription}
            sx={{
              bgcolor: yellowMain,
              color: '#000',
              fontWeight: 800,
              fontSize: '1.1rem',
              py: 1.5,
              borderRadius: 3,
              textTransform: 'none',
              boxShadow: `0 8px 20px ${alpha(yellowMain, 0.4)}`,
              transition: 'all 0.3s ease',
              '&:hover': {
                bgcolor: yellowDark,
                transform: 'translateY(-2px)',
                boxShadow: `0 12px 25px ${alpha(yellowMain, 0.5)}`,
              },
            }}
            startIcon={<Payment />}
          >
            Choose Your Plan
          </Button>
          <Button
            fullWidth
            variant="text"
            onClick={() => navigate('/dashboard')}
            sx={{
              color: 'text.secondary',
              fontWeight: 600,
              fontSize: '0.95rem',
              textTransform: 'none',
              '&:hover': {
                bgcolor: alpha(yellowMain, 0.05),
              }
            }}
          >
            Return to Dashboard
          </Button>
        </Box>
      </DialogContent>

      {/* Subscription Plans Modal */}
      <SubscriptionPlans
        open={showSubscriptionDialog}
        onClose={() => setShowSubscriptionDialog(false)}
        onPlanSelect={handlePlanSelect}
      />

      {/* Payment Modal */}
      {(subscriptionDetails || isProcessingPayment) && selectedPlan && (
        <PaymentModal
          open={showPaymentModal}
          onClose={() => {
            if (!isProcessingPayment) {
              setShowPaymentModal(false)
              setPaymentError(null)
              setSubscriptionDetails(null)
            }
          }}
          onPaymentSuccess={handlePaymentSuccess}
          plan={selectedPlan}
          billingCycle={billingCycle}
          amount={
            subscriptionDetails
              ? parseFloat(
                  subscriptionDetails.client_secret.split('_').pop() || '0'
                ) ||
                (selectedPlan
                  ? billingCycle === 'monthly'
                    ? selectedPlan.monthly_fee
                    : selectedPlan.anual_fee
                  : 0)
              : 0
          }
          therapistFirstName={user?.firstName || ''}
          therapistLastName={user?.lastName || ''}
          therapistEmail={user?.email || ''}
          clientSecret={subscriptionDetails?.client_secret || ''}
          billingAddress={
            therapistProfile?.billingAddressLine ||
            therapistProfile?.addressLine ||
            ''
          }
          billingCity={
            therapistProfile?.billingCity || therapistProfile?.city || ''
          }
          billingState={
            therapistProfile?.billingState || therapistProfile?.state || ''
          }
          billingZip={
            therapistProfile?.billingZipCode || therapistProfile?.zipCode || ''
          }
          isFromOnboarding={true}
          isInitializing={isProcessingPayment}
        />
      )}

      {/* Payment Error Alert */}
      {paymentError && (
        <Fade in timeout={300}>
          <Box sx={{ position: 'fixed', bottom: 20, left: '50%', transform: 'translateX(-50%)', zIndex: 10000, width: '90%', maxWidth: 500 }}>
            <Alert
              severity="error"
              onClose={() => setPaymentError(null)}
              variant="filled"
              sx={{ boxShadow: 3 }}
            >
              {paymentError}
            </Alert>
          </Box>
        </Fade>
      )}
    </Dialog>
  )
}

export default SubscriptionRequiredPopup
